import {Layer, Animation} from "../../../feature/project/Shot";
import {
    Button,
    Flex,
    Modal,
    Space, Tabs,
    Typography
} from "antd";
import React, {useCallback, useEffect} from "react";
import {useAppDispatch, useAppSelector} from "../../../feature/tools";
import {useParams} from "react-router-dom";
import LayerList from "../../common/LayerList";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowUpFromBracket, faPlay,
    faPlus,
    faStop
} from "@fortawesome/free-solid-svg-icons";
import {Starfield} from "../../../components/Starfiled";
import {unsetAnimation, saveAnimation} from "../../../feature/project/animationsSlice";
import {getOffset} from "./ElementContainer";
import {ElementsCollection} from "../../common/ElementsCollection";
import {fetchLayersCollection, fetchLayersLore} from "../../../feature/project/layerSlice";
import {AnimatableElement} from "./AnimatableElement";
import {AnimationForm, compareAnimations} from "./AnimationForm";

const {confirm} = Modal;

export const LayerEditModal = (
    props: {
        componentId: number | undefined,
        layer: Layer,
        onSave: (layer: Layer) => void,
        onCancel: () => void
    }
) => {
    const {projectId, storyId} = useParams()
    const [showSelectLayer, setShowSelectLayer] = React.useState(false)

    useEffect(() => {
        if (!props.layer.id) {
            setShowSelectLayer(true)
        }
    }, [props.layer.id])

    return (
        <>
            <Modal
                open={true}
                title={props?.layer?.id ? 'Edit Layer' : 'Add Layer'}
                width={1000}
                okText={'Save'}
                mask={false}
                maskClosable={false}
                cancelText={'Close'}
                onCancel={props.onCancel}
                okButtonProps={{disabled: true}}
                onOk={() => props.onSave({
                    ...props.layer
                })}
            >
                {showSelectLayer ?
                    <LayerSelectView
                        componentId={props.componentId || 0}
                        onLayerSelected={(layer) => {
                            if (projectId && storyId) {
                                const newLayer = {
                                    ...layer,
                                    id: props.layer.id,
                                    x: undefined,
                                    y: undefined,
                                    z: undefined,
                                }
                                props.onSave(newLayer)
                                setShowSelectLayer(false)
                            }
                        }}/>
                    :
                    <LayerEditView
                        layer={props.layer}
                        onSave={props.onSave}
                        onCancel={props.onCancel}
                        onSelectLayerClick={() => setShowSelectLayer(true)}
                    />}
            </Modal>
        </>
    )
}

const LayerSelectView = (props: { componentId: number, onLayerSelected: (layer: Layer) => void }) => {
    const dispatch = useAppDispatch()
    const {projectId, storyId} = useParams()
    const layersCollection = useAppSelector(state => state.layers.collection)
    const layersLore = useAppSelector(state => state.layers.lore)

    useEffect(() => {
        if (storyId && projectId) {
            dispatch(fetchLayersCollection({projectId: parseInt(projectId), storyId: parseInt(storyId)}))
            dispatch(fetchLayersLore({projectId: parseInt(projectId), componentId: props.componentId}))
        }
    }, [storyId, projectId, props.componentId])

    return (
        <div style={{height: 400}}>
            <Tabs
                defaultActiveKey="1"
                items={[
                    {
                        label: `Collection`,
                        key: "1",
                        children: <ElementsCollection key={"1"}
                                                      animated={true}
                                                      elements={layersCollection?.map(item => {
                                                          return {...item, layers: [item]}
                                                      }) || []}
                                                      onSelect={(element: any) => {
                                                          props.onLayerSelected({
                                                              ...element,
                                                              animations: element.animations?.map((item: Animation) => {
                                                                  return {...item, id: undefined}
                                                              }) || []
                                                          } as Layer)
                                                      }}/>,
                    },
                    {
                        label: `Lore`,
                        key: "2",
                        children: <ElementsCollection key={"2"}
                                                      animated={false}
                                                      elements={layersLore || []}
                                                      onSelect={(element: any) => {
                                                          props.onLayerSelected({
                                                              ...element,
                                                              source: element.source,
                                                              scale: 1,
                                                              animations: element.animations?.map((item: Animation) => {
                                                                  return {...item, id: undefined}
                                                              }) || []
                                                          } as Layer)
                                                      }}/>,
                    },
                ]}
            />
        </div>
    )
}

export const LayerEditView = (props: {
    layer: Layer,
    onSave: (layer: Layer) => void,
    onCancel: () => void,
    onSelectLayerClick: () => void
}) => {
    const dispatch = useAppDispatch()
    const {projectId} = useParams()
    const [scale, setScale] = React.useState(1)
    const [height, setHeight] = React.useState(0)
    const [width, setWidth] = React.useState(0)
    const [source, setSource] = React.useState(props.layer.source)
    const [play, setPlay] = React.useState(false)
    const [animation, setAnimation] = React.useState<Animation | undefined>(undefined)
    const [selectPivotPoint, setSelectPivotPoint] = React.useState(false)

    useEffect(() => {
        setScale(width > height ? width / 1920 : height / 1920)
    }, [width, height, props.layer])

    useEffect(() => {
        setSource(props.layer.source)
        setAnimation(props.layer?.animations?.find((anim: Animation) => anim.id === anim?.id))
    }, [props.layer])

    const elementRef = useCallback((node: HTMLDivElement) => {
        if (!node) return;
        const resizeObserver = new ResizeObserver(() => {
            setWidth(node.clientWidth || 0)
            setHeight(node.clientHeight || 0)
        });
        resizeObserver.observe(node);
    }, []);


    const showConfirmAnimationDeletion = (item: Animation) => {
        confirm({
            title: 'Delete Animation',
            content: 'Are you sure you want to delete this animation?',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id && props.layer?.id) {
                    dispatch(unsetAnimation({
                        projectId: parseInt(projectId),
                        layerId: props.layer.id,
                        animationId: item.id
                    }))
                    setAnimation(undefined)
                }
            },
        });
    }

    return (
        <Flex gap={16}>
            <div ref={elementRef} style={{
                minWidth: 400,
                height: 400,
                borderRadius: 6,
                clipPath: 'border-box',
                position: 'relative'
            }}>
                <Flex ref={elementRef} align={'center'} style={{
                    minWidth: 400,
                    height: 400,
                    borderRadius: 6,
                    clipPath: 'border-box',
                    position: "relative",
                    background: '#eeeeee'
                }}>
                    {props?.layer?.source ?
                        <div style={{
                            width: '100%',
                            height: 'calc(100% / 1.77)',
                            overflow: 'hidden',
                            background: '#000',
                            position: "relative"
                        }}>
                            <AnimatableElement
                                style={
                                    {
                                        transformOrigin: 'top left',
                                        transform: `scale(${scale * (props.layer.scale || 1)})`,
                                        top: 0,
                                        left: 0,
                                    }
                                }
                                scale={scale}
                                element={props.layer}
                                play={play}
                                key={props.layer.id}
                                animations={props.layer.animations && props.layer.animations.length > 0 ? props.layer.animations : [{id: -1}]}
                                onPlayEnd={(id: number) => {
                                    setPlay(false)
                                }}
                            >
                                <img src={props.layer.source}
                                     draggable={false}
                                     unselectable={'on'}
                                     onClick={(e: any) => {
                                         const offset = getOffset(e.target)

                                         const position = {
                                             x: (e.clientX - offset.x) / (scale * (props.layer.scale || 1)),
                                             y: (e.clientY - offset.y) / (scale * (props.layer.scale || 1))
                                         }
                                         if (selectPivotPoint) {
                                             setAnimation(
                                                 {
                                                     ...animation,
                                                     pivotX: position.x,
                                                     pivotY: position.y
                                                 }
                                             )
                                         }
                                     }}
                                />
                            </AnimatableElement>
                        </div>
                        :
                        <div style={{
                            minWidth: 400,
                            height: 'calc(100% / 1.77)',
                            background: "#000000",
                            position: "relative"
                        }}>
                            <Starfield
                                id={"layer-edit-starfield"}
                                starCount={5000}
                                starColor={[255, 255, 255]}
                                speedFactor={0.1}
                                scale={scale}
                                backgroundColor="black"/></div>}

                    <Button icon={<FontAwesomeIcon icon={play ? faStop : faPlay}/>}
                            onClick={() => setPlay(!play)}
                            style={{
                                lineHeight: '12px',
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                zIndex: 10000,
                                width: 38
                            }}
                            disabled={!props.layer.animations || props.layer.animations?.length === 0}
                    />
                    <Button icon={<FontAwesomeIcon icon={faArrowUpFromBracket}/>}
                            style={{position: 'absolute', right: 56, top: 10}}
                            onClick={() => props.onSelectLayerClick()}
                    />
                </Flex></div>
            <div
                style={{width: animation ? 300 : '100%', maxHeight: 400, overflowY: 'auto', scrollbarWidth: "none"}}
            >
                <Flex justify={'space-between'} className={'layer-group-title'} align={'center'}>
                    <Typography style={{fontWeight: 700}}>Animations</Typography>
                    <Button
                        icon={<FontAwesomeIcon icon={faPlus}/>}
                        type={'text'}
                        onClick={() => {
                            setAnimation({})
                        }}
                    />
                </Flex>

                <LayerList
                    is={(item) => animation?.id === item.id}
                    data={props.layer.animations}
                    title={(item) => item.title}
                    clickable={true}
                    onItemClick={(item) => {
                        setAnimation(item)

                    }}
                    onDeleteClick={(item) => {
                        showConfirmAnimationDeletion(item)
                    }}
                    onEditClick={(item) => {
                        setAnimation(item)
                    }}
                />
            </div>

            {animation ?
                <AnimationForm
                    animation={animation}
                    pivotPoint={selectPivotPoint}
                    onSelectPivotPoint={(select: boolean)=> setSelectPivotPoint(select)}
                    wrapper={(children) => {
                        return <Space direction={'vertical'} style={{width: 300, maxHeight: 400, overflowY: 'auto', scrollbarWidth: "none"}}
                                      size={16}>{children} </Space>
                    }}
                    onSave={(animation: Animation) => {
                        const origin = props.layer.animations?.find(item => item.id === animation?.id)
                        if (projectId && props.layer.id && !compareAnimations(origin, animation)) {
                            dispatch(
                                saveAnimation(
                                    {
                                        projectId: parseInt(projectId),
                                        layerId: props.layer.id,
                                        animation: animation
                                    }
                                )
                            )
                        }
                    }}
                    onClose={() => setAnimation(undefined)}
                />
                : ''}
        </Flex>
    )
}


